import { View, Text, Platform } from 'react-native';
import { openExternalUrl } from '@/lib/linking';
import Head from 'expo-router/head';
import AnimatedPressable from '@/components/AnimatedPressable';
import { useRouter } from 'expo-router';
import { Mail, MessageCircle, FileText, ExternalLink } from 'lucide-react-native';
import Animated, { FadeInDown } from 'react-native-reanimated';
import { usePublicThemeColors } from '@/lib/useThemeColors';
import { useTrackScreen } from '@/lib/analytics';
import { PublicLayout } from '@/components/PublicLayout';
import { ContactMessageForm } from '@/components/ContactMessageForm';

export default function ContactScreen() {
  useTrackScreen('Contact');
  const router = useRouter();
  const colors = usePublicThemeColors();

  return (
    <PublicLayout title="Contact Us">
      {Platform.OS === 'web' && (
        <Head>
          <title>Contact Us — Inverio</title>
          <meta name="description" content="Get in touch with Inverio. General support, security inquiries, and company details. Based in Switzerland." />
          <link rel="canonical" href="https://inverio.ai/contact" />
          <meta property="og:title" content="Contact Us — Inverio" />
          <meta property="og:description" content="Get in touch with Inverio. General support, security inquiries, and company details. Based in Switzerland." />
          <meta property="og:url" content="https://inverio.ai/contact" />
        </Head>
      )}
      {/* Intro */}
      <Animated.View entering={FadeInDown.delay(100).duration(500)} className="mb-6">
        <Text style={{ color: colors.text.secondary }} className="text-sm leading-relaxed">
          Have a question, feedback, or need help? We'd love to hear from you. Reach out through any of the channels below and we'll get back to you within one business day.
        </Text>
      </Animated.View>

      {/* Send a Message */}
      <Animated.View entering={FadeInDown.delay(150).duration(500)} className="mb-8">
        <Text style={{ color: colors.text.secondary }} className="text-sm font-semibold uppercase tracking-wide mb-3">
          Send Us a Message
        </Text>
        <ContactMessageForm />
      </Animated.View>

      {/* Contact Cards */}
      <Animated.View entering={FadeInDown.delay(200).duration(500)} className="gap-4 mb-8">
        {/* General Support */}
        <AnimatedPressable
          onPress={() => openExternalUrl('mailto:support@inverio.ai', 'Contact.email')}
          className="flex-row gap-4 p-5 rounded-2xl"
          style={{
            backgroundColor: colors.surface.primary,
            borderWidth: 1,
            borderColor: colors.border.light,
          }}
        >
          <View
            className="w-12 h-12 rounded-xl items-center justify-center"
            style={{ backgroundColor: colors.primary[50] }}
          >
            <Mail size={24} color={colors.primary[600]} />
          </View>
          <View className="flex-1">
            <Text style={{ color: colors.text.primary }} className="font-semibold mb-1">
              General Support
            </Text>
            <Text style={{ color: colors.primary[600] }} className="text-sm">
              support@inverio.ai
            </Text>
            <Text style={{ color: colors.text.tertiary }} className="text-xs mt-1">
              Account help, billing, platform questions
            </Text>
          </View>
          <ExternalLink size={16} color={colors.text.tertiary} style={{ marginTop: 4 }} />
        </AnimatedPressable>

        {/* Security */}
        <AnimatedPressable
          onPress={() => openExternalUrl('mailto:security@inverio.ai', 'Contact.email')}
          className="flex-row gap-4 p-5 rounded-2xl"
          style={{
            backgroundColor: colors.surface.primary,
            borderWidth: 1,
            borderColor: colors.border.light,
          }}
        >
          <View
            className="w-12 h-12 rounded-xl items-center justify-center"
            style={{ backgroundColor: colors.primary[50] }}
          >
            <FileText size={24} color={colors.primary[600]} />
          </View>
          <View className="flex-1">
            <Text style={{ color: colors.text.primary }} className="font-semibold mb-1">
              Security & Privacy
            </Text>
            <Text style={{ color: colors.primary[600] }} className="text-sm">
              security@inverio.ai
            </Text>
            <Text style={{ color: colors.text.tertiary }} className="text-xs mt-1">
              Vulnerability reports, data requests, FADP/GDPR inquiries
            </Text>
          </View>
          <ExternalLink size={16} color={colors.text.tertiary} style={{ marginTop: 4 }} />
        </AnimatedPressable>

        {/* Help Center */}
        <AnimatedPressable
          onPress={() => router.push('/help')}
          className="flex-row gap-4 p-5 rounded-2xl"
          style={{
            backgroundColor: colors.surface.primary,
            borderWidth: 1,
            borderColor: colors.border.light,
          }}
        >
          <View
            className="w-12 h-12 rounded-xl items-center justify-center"
            style={{ backgroundColor: colors.primary[50] }}
          >
            <MessageCircle size={24} color={colors.primary[600]} />
          </View>
          <View className="flex-1">
            <Text style={{ color: colors.text.primary }} className="font-semibold mb-1">
              Help Center
            </Text>
            <Text style={{ color: colors.primary[600] }} className="text-sm">
              Browse FAQs & guides
            </Text>
            <Text style={{ color: colors.text.tertiary }} className="text-xs mt-1">
              Find answers to common questions
            </Text>
          </View>
        </AnimatedPressable>
      </Animated.View>

      {/* Company Details */}
      <Animated.View entering={FadeInDown.delay(300).duration(500)}>
        <Text style={{ color: colors.text.secondary }} className="text-sm font-semibold uppercase tracking-wide mb-3">
          Company Details
        </Text>
        <View
          className="p-5 rounded-2xl"
          style={{
            backgroundColor: colors.surface.primary,
            borderWidth: 1,
            borderColor: colors.border.light,
          }}
        >
          <Text style={{ color: colors.text.primary }} className="font-bold mb-2">
            Inverio
          </Text>
          <Text style={{ color: colors.text.secondary }} className="text-sm leading-relaxed mb-1">
            A service of A. Kouadio INVARK · Geneva, Switzerland
          </Text>
          <Text style={{ color: colors.text.secondary }} className="text-sm leading-relaxed mb-3">
            Registered under Swiss law
          </Text>
          <View style={{ borderTopWidth: 1, borderTopColor: colors.border.light, paddingTop: 12 }}>
            <Text style={{ color: colors.text.tertiary }} className="text-xs leading-relaxed">
              Response times: We aim to respond to all inquiries within one business day. For urgent security matters, please mark your email as urgent.
            </Text>
          </View>
        </View>
      </Animated.View>
    </PublicLayout>
  );
}
